/**
 * SMS Service Module
 * Provides functionality for SMS-based operations including OTP verification
 * and transaction notifications. Uses Parse Cloud Functions for SMS delivery.
 */

import Parse from "./parse.service";

/**
 * Sends an OTP (One Time Password) to the specified mobile number
 * @param {string} mobile - Mobile number to send OTP to
 * @returns {Promise<Object>} Response from the SMS service
 * @throws {Error} If OTP sending fails
 */
export const sendOTP = async (mobile) => {
  const params = { mobile: mobile };
  try {
      const result = await Parse.Cloud.run("sendOTP", params)
      return result;
  } catch (err) {
      return err;
  }
};

/**
 * Resends an OTP to the specified mobile number
 * @param {string} mobile - Mobile number to resend OTP to
 * @param {string} type - Type of OTP to resend
 * @returns {Promise<Object>} Response from the SMS service
 * @throws {Error} If OTP resending fails
 */
export const resendOTP = async (mobile, type) => {
  const params = { mobile: mobile, OTPtype: type };
  try {
      const result = await Parse.Cloud.run("resendOTP", params)
      return result;
  } catch (err) {
      return err;
  }
};

/**
 * Verifies an OTP entered by the user
 * @param {string} mobile - Mobile number associated with the OTP
 * @param {string} otp - OTP entered by the user
 * @returns {Promise<Object>} Verification result
 * @throws {Error} If verification fails
 */
export const verifyOTP = async (mobile, otp) => {
  const params = { mobile: mobile, otp: otp };
  try {
      const result = await Parse.Cloud.run("verifyUser", params)
      return result;
  } catch (err) {
      return err;
  }
};

/**
 * Sends an SMS notification for a payment made
 * @param {number} amount - Amount paid
 * @param {string} party - Name of the party involved
 * @param {string} url - URL for transaction details
 * @param {string} mobile - Mobile number to send SMS to
 * @returns {Promise<Object>} Response from the SMS service
 * @throws {Error} If SMS sending fails
 */
export const sendPaidSMS = async (amount, party, url, mobile) => {
  const params = { amount: amount, party: party, url: url, mobile: mobile };
  try {
      const result = await Parse.Cloud.run("PaidSMS", params);
      return result;
  } catch (err) {
      return err;
  }
};

/**
 * Sends an SMS notification for a payment received
 * @param {number} amount - Amount received
 * @param {string} party - Name of the party involved
 * @param {string} url - URL for transaction details
 * @param {string} mobile - Mobile number to send SMS to
 * @returns {Promise<Object>} Response from the SMS service
 * @throws {Error} If SMS sending fails
 */
export const sendReceiveSMS = async (amount, party, url, mobile) => {
  const params = { amount: amount, party: party, url: url, mobile: mobile };
  try {
      const result = await Parse.Cloud.run("ReceiveSMS", params);
      return result;
  } catch (err) {
      return err;
  }
};
  